import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProductTour, { hasSeenTour } from './ProductTour';
import './Layout.css';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, org, logout, isAdmin } = useAuth();

  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings')
  );

  // Auto-launch the tour once (per browser) the first time an ADMIN lands
  // in the app after onboarding. Managers/Operators don't get the full nav
  // the tour points at, so it only auto-fires for admins. Replayable for
  // anyone via the footer "? Tour" button -> setTourOpen(true).
  const [tourOpen, setTourOpen] = useState(false);

  // Fire once auth has resolved (isAdmin can be false on the first render
  // while AuthContext is still loading, so we wait for it in an effect).
  useEffect(() => {
    if (isAdmin && !hasSeenTour()) setTourOpen(true);
  }, [isAdmin]);

  // Map each nav path to a data-tour key used by ProductTour's steps.
  const tourKeys = {
    '/dashboard': 'dashboard',
    '/queue': 'queue',
    '/jobs': 'jobs',
    '/inventory': 'inventory',
    '/departments': 'departments',
    '/locations': 'locations',
    '/floor-audit': 'floor-audit',
    '/audit': 'audit-log',
  };

  const navItems = [
    { label: 'Dashboard',   path: '/dashboard' },
    { label: 'Queue',       path: '/queue' },
    { label: 'Jobs',        path: '/jobs' },
    { label: 'Inventory',   path: '/inventory' },
    { label: 'Departments', path: '/departments' },
    { label: 'Locations',   path: '/locations' },
    { label: 'Floor Audit', path: '/floor-audit' },
    { label: 'Audit Log',   path: '/audit' },
  ];

  const settingsItems = [
    { label: 'Organization',    path: '/settings/organization' },
    { label: 'Team',            path: '/settings/team' },
    { label: 'Kiosk Settings',  path: '/settings/kiosk' },
    { label: 'Billing',         path: '/settings/billing' },
    { label: 'Danger Zone',     path: '/settings/danger' },
  ];

  const isActive = (path) => location.pathname === path;
  const isSettings =
    location.pathname.startsWith('/settings') && !isActive('/settings/kiosk');

  return (
    <div className="layout">
      <aside className="layout-sidebar">
        <div className="layout-logo">
          TRACE<span className="layout-sub">OPS</span>
        </div>

        <nav className="layout-nav">
          {navItems.map((item) => (
            <div
              key={item.path}
              data-tour={tourKeys[item.path]}
              className={`layout-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </div>
          ))}

          <div
            data-tour="settings"
            className={`layout-nav-item settings-toggle ${isSettings ? 'active' : ''}`}
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            <span>Settings</span>
            <span className={`settings-chevron ${settingsOpen ? 'open' : ''}`}>›</span>
          </div>

          {settingsOpen && (
            <div className="settings-subnav">
              {settingsItems.map((item) => (
                <div
                  key={item.path}
                  className={`settings-subnav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </nav>

                <div className="layout-footer">
          {/* Replay the product tour any time */}
          <button
            className="layout-tour-replay"
            onClick={() => setTourOpen(true)}
          >
            <span className="layout-tour-replay-icon">?</span>
            Replay tour
          </button>

          <div className="layout-user-email">{user?.email}</div>
          <div className="layout-user-org">{org?.name}</div>
          <button className="layout-logout" onClick={logout}>Sign out</button>
        </div>
      </aside>

      <main className="layout-main">{children}</main>

      <ProductTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        accentColor={org?.accentColor}
      />
    </div>
  );
}